'use client';

/**
 * Route-level not-found factory (doc 13 §4.4).
 *
 * A missing branch, order, menu, quote or invoice is a *normal outcome* —
 * expired links, deleted records, mistyped codes — not an exception. This
 * factory encodes the shared contract: explain what was not found in plain
 * language and offer the nearest valid destination.
 *
 * Oracle rule (§9): the copy never echoes the identifier ("Order 123 not
 * found" for someone else's valid order and for a nonexistent one must be
 * indistinguishable), so the defaults are deliberately generic. Override the
 * title/message only with copy that stays true for any id.
 *
 * Variants:
 *   · `guest`    — neutral full-screen card, escape to home.
 *   · `operator` — card inside the dashboard shell, escape to dashboard.
 *   · `pos`      — card inside the POS shell, escape to the branch home
 *                  (derived from the pathname).
 *
 * Not-found is visually distinct from error: muted icon, no destructive tone,
 * no retry — there is nothing to retry, the destination is gone.
 */

import { type LucideIcon, SearchX } from 'lucide-react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';

import { Button } from '@/components/ui/button';
import { cn } from '@/components/ui/utils';
import { dashboardHref, posHref } from '@/lib/routing/platform-hosts';

export type RouteNotFoundVariant = 'guest' | 'operator' | 'pos';

export type RouteNotFoundOptions = {
  variant: RouteNotFoundVariant;
  title?: string;
  message?: string;
  escapeHref?: string;
  escapeLabel?: string;
  icon?: LucideIcon;
};

const DEFAULT_COPY: Record<
  RouteNotFoundVariant,
  { title: string; message: string; escapeLabel: string }
> = {
  guest: {
    title: 'We couldn’t find this page',
    message: 'It may have been removed, expired, or the link may be wrong.',
    escapeLabel: 'Go to home',
  },
  operator: {
    title: 'This record doesn’t exist',
    message: 'It may have been deleted or the link may be wrong.',
    escapeLabel: 'Back to dashboard',
  },
  pos: {
    title: 'This page isn’t available',
    message: 'The branch or record may have been removed, or the link may be wrong.',
    escapeLabel: 'Back to branch',
  },
};

function defaultEscapePath(pathname: string, variant: RouteNotFoundVariant): string | undefined {
  if (variant === 'pos') {
    const branchId = pathname.split('/')[2];
    return branchId ? posHref(branchId, '/') : dashboardHref('/');
  }
  if (variant === 'operator') return dashboardHref('/');
  return '/';
}

export function RouteNotFoundScreen({
  variant,
  title,
  message,
  escapeHref,
  escapeLabel,
  icon: Icon = SearchX,
  className,
}: {
  variant: RouteNotFoundVariant;
  title: string;
  message?: string;
  escapeHref?: string;
  escapeLabel?: string;
  icon?: LucideIcon;
  className?: string;
}) {
  const heading = (
    <div className="text-center">
      <span className="mx-auto flex size-14 items-center justify-center rounded-full bg-[var(--color-bg-sunken)] text-[var(--color-text-secondary)]">
        <Icon className="size-7" aria-hidden="true" />
      </span>
      <h1 className="mt-5 font-display font-semibold tracking-tight">{title}</h1>
      {message ? (
        <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-[var(--color-text-secondary)]">{message}</p>
      ) : null}
    </div>
  );

  const action = escapeHref ? (
    <div className={cn('mt-6', variant === 'pos' && 'space-y-2.5')}>
      <Button asChild className={cn(variant === 'pos' && 'h-12 w-full text-base')}>
        <Link href={escapeHref}>{escapeLabel || 'Go back'}</Link>
      </Button>
    </div>
  ) : null;

  if (variant === 'guest') {
    return (
      <div className={cn('flex min-h-screen items-center justify-center bg-[var(--color-bg-base)] px-4 py-16 sm:py-24', className)}>
        <div className="w-full max-w-md">
          <h2 className="sr-only">Page not found</h2>
          {heading}
          {action}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex min-h-[60vh] items-center justify-center px-4 py-10', className)}>
      <div className="w-full max-w-xl rounded-3xl border border-[var(--dm-card-border)]/80 bg-card p-6 sm:p-8">
        {heading}
        {action}
      </div>
    </div>
  );
}

export function createRouteNotFound(options: RouteNotFoundOptions) {
  const {
    variant,
    title = DEFAULT_COPY[variant].title,
    message = DEFAULT_COPY[variant].message,
    escapeLabel = DEFAULT_COPY[variant].escapeLabel,
    escapeHref: explicitEscape,
    icon,
  } = options;

  return function RouteNotFound() {
    const pathname = usePathname();
    const escapeHref = explicitEscape ?? defaultEscapePath(pathname, variant);
    return (
      <RouteNotFoundScreen
        variant={variant}
        title={title}
        message={message}
        escapeHref={escapeHref}
        escapeLabel={escapeLabel}
        icon={icon}
      />
    );
  };
}
